import { useState } from "react";
import { describeDice, diceDistribution } from "./resultLogic";
import type { DiceBar } from "./resultLogic";
import { linearScale, niceScale, roundedTopBar } from "./chartMath";
import { ChartFrame, ChartTooltip } from "./ChartFrame";
import { useElementWidth } from "./useElementWidth";

const MARGIN = { top: 20, right: 8, bottom: 28, left: 40 };
const BAR_MAX = 24;

const fmt1 = (n: number) => n.toLocaleString("de-DE", { maximumFractionDigits: 1 });

/** Column chart of how often each dice sum (2 to 12) was rolled, with the count a fair pair of dice would give. */
export function DiceChart({ diceSums }: { diceSums: number[] }) {
  const { bars, total } = diceDistribution(diceSums);
  const [wrapRef, width] = useElementWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);

  const height = width < 420 ? 200 : 240;
  const maxValue = Math.max(1, ...bars.map((b) => Math.max(b.count, b.expected)));
  const scale = niceScale(maxValue, 4, true);
  const plotW = width - MARGIN.left - MARGIN.right;
  const band = plotW / bars.length;
  const barW = Math.min(BAR_MAX, band * 0.62);
  const y = linearScale(0, scale.max, height - MARGIN.bottom, MARGIN.top);
  const baseline = height - MARGIN.bottom;
  const topCount = bars.reduce((best, b) => (b.count > best.count ? b : best), bars[0]!);
  const hovered: DiceBar | null = hover !== null ? (bars[hover] ?? null) : null;

  return (
    <ChartFrame
      id="dice"
      title="Würfelverteilung"
      subtitle={total === 0 ? "Es wurde noch nicht gewürfelt." : `Anzahl der Würfe je Augensumme, insgesamt ${total.toLocaleString("de-DE")}.`}
      legend={
        total > 0 ? (
          <ul className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-ink-2">
            <li className="flex items-center gap-2">
              <span aria-hidden="true" className="inline-block h-3.5 w-3.5 rounded-t-[4px] bg-accent" />
              Gewürfelt
            </li>
            <li className="flex items-center gap-2">
              <span aria-hidden="true" className="inline-block h-0.5 w-4 bg-ink" />
              Erwartet bei fairen Würfeln
            </li>
          </ul>
        ) : undefined
      }
      table={{
        caption: "Würfelsummen: gewürfelt und erwartet",
        head: ["Augensumme", "Gewürfelt", "Erwartet"],
        rows: bars.map((b) => [b.sum, b.count, fmt1(b.expected)]),
      }}
    >
      <div ref={wrapRef} className="relative">
        <svg
          role="img"
          aria-label={describeDice(bars, total)}
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          className="block max-w-full"
          onPointerLeave={() => setHover(null)}
        >
          {scale.ticks.map((t) => (
            <g key={t}>
              <line x1={MARGIN.left} x2={width - MARGIN.right} y1={y(t)} y2={y(t)} className="stroke-line" strokeWidth={1} />
              <text x={MARGIN.left - 8} y={y(t)} dy="0.32em" textAnchor="end" className="fill-ink-2 text-[11px] tabular-nums">
                {t}
              </text>
            </g>
          ))}

          {bars.map((b, i) => {
            const cx = MARGIN.left + band * i + band / 2;
            const top = y(b.count);
            const active = hover === i;
            return (
              <g key={b.sum}>
                {b.count > 0 && (
                  <path
                    d={roundedTopBar(cx - barW / 2, top, barW, baseline - top, 4)}
                    className={active ? "fill-ink" : "fill-accent"}
                  />
                )}
                <line
                  x1={cx - barW / 2 - 3}
                  x2={cx + barW / 2 + 3}
                  y1={y(b.expected)}
                  y2={y(b.expected)}
                  className="stroke-ink"
                  strokeWidth={2}
                  strokeLinecap="round"
                />
                <text x={cx} y={height - 8} textAnchor="middle" className="fill-ink-2 text-[11px]">
                  {b.sum}
                </text>
                {total > 0 && b.sum === topCount.sum && b.count > 0 && (
                  <text x={cx} y={Math.min(top, y(b.expected)) - 6} textAnchor="middle" className="fill-ink text-[11px] font-semibold tabular-nums">
                    {b.count}
                  </text>
                )}
                <rect
                  x={cx - band / 2}
                  y={MARGIN.top - 8}
                  width={band}
                  height={baseline - MARGIN.top + 8}
                  fill="transparent"
                  onPointerEnter={() => setHover(i)}
                  onPointerDown={() => setHover(i)}
                />
              </g>
            );
          })}
          <line x1={MARGIN.left} x2={width - MARGIN.right} y1={baseline} y2={baseline} className="stroke-ink-2" strokeWidth={1} />
        </svg>
        {hovered && (
          <ChartTooltip x={MARGIN.left + band * (hovered.sum - 2) + band / 2} width={width}>
            <p className="font-semibold">Augensumme {hovered.sum}</p>
            <p className="tabular-nums">Gewürfelt: {hovered.count}</p>
            <p className="tabular-nums text-ink-2">Erwartet: {fmt1(hovered.expected)}</p>
          </ChartTooltip>
        )}
      </div>
    </ChartFrame>
  );
}
